/**
 * ActionIntentModal — pre-commit confirmation for any Flight Deck lever
 * that mutates state or costs money. Shows:
 *   • the lever label + pink why-it-works explainer
 *   • a precise preview of who it'll hit + $ cost breakdown
 *   • optional inline inputs (new angle / new post time / CTA copy / bonus)
 *   • Confirm (one click) / Cancel (esc or backdrop)
 *
 * Skips the modal for zero-cost, zero-input levers via `shouldConfirm(action)`.
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { X, AlertTriangle, ChevronRight, Users, DollarSign } from 'lucide-react';
import CreatorAvatar from './CreatorAvatar';

const ANGLES = ['Tutorial', 'POV', 'Confession', 'Reaction', 'Day-in-the-Life'];

// ── which levers always get the modal ──
const ALWAYS_CONFIRM = new Set([
  'spawn-burst', 'story-extension', 'whitelist-spark', 'creator-swap',
  'pivot-angle', 'shift-time', 'pin-comment-cta', 'pause-wave', 'resume-wave',
]);

export function shouldConfirm(path) {
  return ALWAYS_CONFIRM.has(path);
}

// Rough client-side preview — server is authoritative, this is just UX hint.
function previewTargets(path, creators = [], inputs = {}) {
  const live = creators.filter(c => c.status === 'live');
  const pending = creators.filter(c => c.status !== 'live');
  if (path === 'hero-mirror') return { list: live, note: 'All currently-live creators' };
  if (path === 'reply-storm') {
    const prompters = creators.filter(c => c.role === 'engagement_prompter');
    return { list: prompters.length ? prompters : live, note: prompters.length ? 'Engagement-prompter role' : 'All live creators (no prompters assigned)' };
  }
  if (path === 'story-extension') return { list: live, note: `Offer bonus to ${live.length} live creator(s)` };
  if (path === 'whitelist-spark') return { list: live.slice(0, 1), note: 'Top live creator' };
  if (path === 'pin-comment-cta') return { list: live, note: 'All live creators' };
  if (path === 'pivot-angle') return { list: pending, note: 'Not-yet-posted creators only' };
  if (path === 'shift-time') return { list: pending, note: 'Not-yet-posted creators only' };
  if (path === 'creator-swap') return { list: pending.slice(0, 1), note: 'Pulls 1 pending creator' };
  if (path === 'pause-wave') return { list: creators, note: 'All creators in the wave' };
  if (path === 'resume-wave') return { list: creators, note: 'All creators in the wave' };
  if (path === 'spawn-burst') {
    const n = Math.max(1, Math.min(10, Number(inputs.new_count) || 3));
    return { list: [], note: `${n} new creator(s) from your wait-list cohort`, placeholderCount: n };
  }
  return { list: [], note: '' };
}

function previewCost(path, targets, inputs = {}) {
  const n = targets.list?.length || 0;
  const extra = targets.placeholderCount || 0;
  if (path === 'spawn-burst') return { total: extra * 1200, line: `${extra} × $1,200 = $${(extra * 1200).toLocaleString()}` };
  if (path === 'story-extension') {
    const bonus = Number(inputs.bonus_usd) || 150;
    return { total: n * bonus, line: `${n} × $${bonus} bonus = $${(n * bonus).toLocaleString()}` };
  }
  if (path === 'whitelist-spark') {
    const budget = Number(inputs.budget_usd) || 500;
    return { total: budget, line: `$${budget.toLocaleString()} Spark Ad budget` };
  }
  if (path === 'creator-swap') return { total: 800, line: '$800 replacement fee' };
  return { total: 0, line: 'Free' };
}

const META = {
  'hero-mirror':     { label: 'Hero Mirror',         why: 'Brand re-shares the top post within 7 min → historical +2.1× reach lift.' },
  'reply-storm':     { label: 'Reply Storm',         why: 'Prompters flood the comment section of the top post to re-ignite algorithm signal.' },
  'spawn-burst':     { label: 'Spawn Burst',         why: 'Pull fresh creators from your wait-list cohort. They post within 2–4 hrs to extend momentum.' },
  'story-extension': { label: 'Story Extension',     why: 'Ask live creators to post Story/Reel follow-ups beyond the original deliverable for a cash bonus.' },
  'pivot-angle':     { label: 'Pivot Angle',         why: 'Change the content angle for not-yet-posted creators (e.g. Tutorial → Confession) before they shoot.' },
  'whitelist-spark': { label: 'Whitelist Spark Ad',  why: 'Get the top creator to whitelist their post so you can boost it as a paid Spark / Partnership Ad.' },
  'pin-comment-cta': { label: 'Pin CTA Comment',     why: 'Ask creators to pin a specific comment to drive comment-velocity lift.' },
  'shift-time':      { label: 'Shift Post Time',     why: 'Reschedule a not-yet-posted burst to a better algorithm window detected from live data.' },
  'creator-swap':    { label: 'Creator Swap',        why: 'Pull a replacement creator in if an assigned one has gone quiet 6+hr before their slot.' },
  'pause-wave':      { label: 'Pause Wave',          why: 'Halt remaining bursts. Every creator is notified. Unsent spend is refunded.' },
  'resume-wave':     { label: 'Resume Wave',         why: 'Re-enable the paused wave. Every creator is notified they are cleared to post again.' },
};

export default function ActionIntentModal({ path, creators = [], onConfirm, onClose }) {
  const [inputs, setInputs] = useState({});
  const targets = useMemo(() => previewTargets(path, creators, inputs), [path, creators, inputs]);
  const cost = useMemo(() => previewCost(path, targets, inputs), [path, targets, inputs]);
  const meta = META[path] || { label: path, why: '' };

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose?.();
  }, [onClose]);
  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const handleConfirm = () => {
    const body = { params: {}, creator_handles: undefined, assignment_ids: undefined };
    if (path === 'spawn-burst') body.params.new_creator_count = Number(inputs.new_count) || 3;
    if (path === 'story-extension') body.params.bonus_usd = Number(inputs.bonus_usd) || 150;
    if (path === 'whitelist-spark') body.params.budget_usd = Number(inputs.budget_usd) || 500;
    if (path === 'pivot-angle' && inputs.new_angle) body.new_angle = inputs.new_angle;
    if (path === 'shift-time' && inputs.new_post_time) body.new_post_time = new Date(inputs.new_post_time).toISOString();
    if (path === 'pin-comment-cta' && inputs.cta_text) body.params.cta_text = inputs.cta_text;
    onConfirm?.(path, body);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="action-intent-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0F0F0F] border border-[#1f1f1f] rounded-2xl w-full max-w-lg overflow-hidden"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
      >
        <header className="flex items-start justify-between gap-3 p-5 border-b border-[#1f1f1f]">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF4D94] font-bold">Confirm lever</p>
            <h3 className="text-lg font-black text-white mt-0.5">{meta.label}</h3>
            <p className="text-[11px] text-gray-400 leading-relaxed mt-1">{meta.why}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="intent-modal-close"
                  className="w-8 h-8 rounded-full bg-[#1f1f1f] hover:bg-[#2a2a2a] flex items-center justify-center text-gray-400 flex-shrink-0">
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* who */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-3.5 w-3.5 text-[#00D4FF]" />
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Who it'll touch</p>
            </div>
            <div className="rounded-xl p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
              {targets.list?.length ? (
                <div className="flex items-center gap-2 flex-wrap">
                  {targets.list.slice(0, 8).map((c) => (
                    <span key={c.assignment_id || c.creator_handle} className="inline-flex items-center gap-1.5 pl-0.5 pr-2.5 py-0.5 rounded-full text-[10px] bg-[#121212] border border-[#1f1f1f] text-gray-300">
                      <CreatorAvatar handle={c.creator_handle} src={c.creator_avatar} size={18} />
                      {c.creator_handle}
                    </span>
                  ))}
                  {targets.list.length > 8 && <span className="text-[10px] text-gray-500">+{targets.list.length - 8} more</span>}
                </div>
              ) : (
                <p className="text-[11px] text-gray-400">{targets.placeholderCount ? `${targets.placeholderCount} new creator(s)` : 'No creators affected.'}</p>
              )}
              <p className="text-[10px] text-gray-500 mt-2">{targets.note}</p>
            </div>
          </section>

          {/* inputs */}
          {path === 'spawn-burst' && (
            <NumberRow label="How many new creators?" value={inputs.new_count ?? 3}
                       onChange={(v) => setInputs(x => ({ ...x, new_count: v }))}
                       min={1} max={10} testid="intent-spawn-count" />
          )}
          {path === 'story-extension' && (
            <NumberRow label="Bonus per creator (USD)" value={inputs.bonus_usd ?? 150}
                       onChange={(v) => setInputs(x => ({ ...x, bonus_usd: v }))}
                       min={50} max={1000} step={25} testid="intent-bonus-usd" />
          )}
          {path === 'whitelist-spark' && (
            <NumberRow label="Spark Ad budget (USD)" value={inputs.budget_usd ?? 500}
                       onChange={(v) => setInputs(x => ({ ...x, budget_usd: v }))}
                       min={100} max={50000} step={100} testid="intent-spark-budget" />
          )}
          {path === 'pivot-angle' && (
            <SelectRow label="New content angle" value={inputs.new_angle || 'Confession'}
                       onChange={(v) => setInputs(x => ({ ...x, new_angle: v }))}
                       options={ANGLES} testid="intent-new-angle" />
          )}
          {path === 'shift-time' && (
            <TextRow label="New post time (ISO)" type="datetime-local" value={inputs.new_post_time || ''}
                     onChange={(v) => setInputs(x => ({ ...x, new_post_time: v }))}
                     placeholder="2026-04-22T18:30" testid="intent-new-time" />
          )}
          {path === 'pin-comment-cta' && (
            <TextRow label="CTA comment text" value={inputs.cta_text || ''}
                     onChange={(v) => setInputs(x => ({ ...x, cta_text: v }))}
                     placeholder="drop your routine below 👇" testid="intent-cta-text" />
          )}

          {/* cost */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-3.5 w-3.5 text-[#FFB84D]" />
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold">Cost preview</p>
            </div>
            <div className="rounded-xl p-3 bg-[#0A0A0A] border border-[#1f1f1f] flex items-baseline justify-between">
              <p className="text-[11px] text-gray-400">{cost.line}</p>
              <p className="text-xl font-black tabular-nums" style={{ color: cost.total ? '#FFB84D' : '#4ade80' }}>
                {cost.total ? `$${cost.total.toLocaleString()}` : 'Free'}
              </p>
            </div>
          </section>

          {/* warning for destructive */}
          {(path === 'pause-wave' || path === 'creator-swap') && (
            <div className="flex items-start gap-2 rounded-lg p-2.5 bg-[#FF6B6B10] border border-[#FF6B6B30]">
              <AlertTriangle className="h-3.5 w-3.5 text-[#FF6B6B] flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#FF6B6B] leading-snug">
                {path === 'pause-wave'
                  ? 'This halts all remaining bursts. Every creator in the wave is notified.'
                  : 'Pulled creators are notified their slot was withdrawn. No penalty on them.'}
              </p>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 border-t border-[#1f1f1f] bg-[#0A0A0A]">
          <button type="button" onClick={onClose} data-testid="intent-cancel"
                  className="px-4 py-2 rounded-lg text-[11px] font-semibold text-gray-400 hover:text-white hover:bg-[#1f1f1f] transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} data-testid="intent-confirm"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-[11px] font-bold text-[#0A0A0A] bg-[#FF4D94] hover:bg-[#ff69a6] transition-colors">
            Confirm {meta.label} <ChevronRight className="h-3 w-3" />
          </button>
        </footer>
      </div>
    </div>
  );
}

function NumberRow({ label, value, onChange, min = 0, max = 10000, step = 1, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold block mb-1.5">{label}</label>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(e.target.value)}
             data-testid={testid}
             className="w-full px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] text-white text-sm font-semibold tabular-nums focus:border-[#00D4FF]/50 focus:outline-none" />
    </div>
  );
}

function TextRow({ label, value, onChange, type = 'text', placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold block mb-1.5">{label}</label>
      <input type={type} value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             data-testid={testid}
             className="w-full px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] text-white text-sm focus:border-[#00D4FF]/50 focus:outline-none" />
    </div>
  );
}

function SelectRow({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-gray-500 font-bold block mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="w-full px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] text-white text-sm focus:border-[#00D4FF]/50 focus:outline-none">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
